// NOTE: This file is mirrored byte-identical at
// selfservice-api/src/shared/creative-concurrency.ts. Keep the two in sync.
// See ADR CTV-506.

export const MAX_CONCURRENT_CREATIVES = 8; // Publica's AdserverCreatives[] cap.

export interface ConcurrencyCreative {
  startDate: string;
  endDate: string;
}

export interface OverCapRange {
  startDate: string;
  endDate: string;
  count: number;
}

export interface ConcurrencyResult {
  withinCap: boolean;
  overCapRanges: OverCapRange[];
}

export function validateCreativeConcurrency(args: {
  windowStart: string;
  windowEnd: string;
  creatives: ConcurrencyCreative[];
}): ConcurrencyResult {
  const { windowStart, windowEnd, creatives } = args;
  const windowStartT = parseDate(windowStart);
  const windowEndT = parseDate(windowEnd);
  if (windowStartT == null || windowEndT == null || windowEndT < windowStartT) {
    return { withinCap: true, overCapRanges: [] };
  }

  const boundaries = new Set<number>();
  boundaries.add(windowStartT);
  boundaries.add(windowEndT + DAY_MS);
  const intervals: [number, number][] = [];
  for (const c of creatives) {
    const s = parseDate(c.startDate);
    const e = parseDate(c.endDate);
    if (s == null || e == null || e < s) continue;
    const lo = Math.max(s, windowStartT);
    const hi = Math.min(e, windowEndT);
    if (hi < lo) continue;
    intervals.push([lo, hi]);
    boundaries.add(lo);
    boundaries.add(hi + DAY_MS);
  }
  const sorted = Array.from(boundaries).sort((a, b) => a - b);

  const ranges: OverCapRange[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const segStart = sorted[i];
    const segEndExclusive = sorted[i + 1];
    if (segStart < windowStartT || segEndExclusive > windowEndT + DAY_MS) continue;
    if (segStart >= segEndExclusive) continue;
    let count = 0;
    for (const [s, e] of intervals) {
      if (s <= segStart && e + DAY_MS >= segEndExclusive) count++;
    }
    if (count <= MAX_CONCURRENT_CREATIVES) continue;
    const segEnd = segEndExclusive - DAY_MS;
    const last = ranges[ranges.length - 1];
    if (last && parseDate(last.endDate)! + DAY_MS === segStart) {
      last.endDate = formatDate(segEnd);
      if (count > last.count) last.count = count;
    } else {
      ranges.push({
        startDate: formatDate(segStart),
        endDate: formatDate(segEnd),
        count,
      });
    }
  }
  return { withinCap: ranges.length === 0, overCapRanges: ranges };
}

const DAY_MS = 86_400_000;

function parseDate(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).slice(0, 10));
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const t = Date.UTC(y, mo - 1, d);
  const back = new Date(t);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
    return null;
  }
  return t;
}

function formatDate(t: number): string {
  const d = new Date(t);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}
